import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { FC, useEffect } from 'react';
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { IconWithTooltip } from 'src/components/shared/IconWithTooltip';
import { isObjectEmpty, isUrl } from 'src/helpers';
import { setNotice } from 'src/redux/noticesSlice';
import { IClientFull, useUpdateClientMutation } from 'src/redux/services/clients';
import * as yup from 'yup';
import { CLIENT_ID } from '../../../constants';
import {
  EAuthMethodType,
  EGrantTypes,
  EResponseTypes,
  ESigningAlgTypes,
  ESubjectTypeVariant,
  logoutUriSchema,
  redirectUriSchema,
  requestUriSchema,
} from '../../applications/old/CreateApplication';
import { CustomSelect } from '../../custom/CustomSelect';
import { CustomTypography } from '../../custom/CustomTypography';
import { PasswordTextfield } from '../../custom/PasswordTextfield';
import { SettingsItem } from '../SettingsItem';
import styles from './ClientSettings.module.css';
import { ArrayTextFields } from './ClientSettingsFields';
import { ENoticeType } from 'src/enums';

interface IClientSettingsHeaderProps {
  client: IClientFull;
}

export const ClientSettingsParams: FC<IClientSettingsHeaderProps> = ({ client }) => {
  const dispatch = useDispatch();
  const [updateClient, updateClientResult] = useUpdateClientMutation();

  type TClientSettingsInputs = {
    client_id: string;
    client_secret: string;
    domain: string;
    authorize_only_admins: boolean;
    redirect_uris: {
      value: string;
    }[];
    post_logout_redirect_uris: {
      value: string;
    }[];
    request_uris: {
      value: string;
    }[];
    response_types: string[];
    grant_types: string[];
    token_endpoint_auth_method: string;
    introspection_endpoint_auth_method: string;
    revocation_endpoint_auth_method: string;
    id_token_signed_response_alg: string;
    require_auth_time: boolean;
    subject_type: string;
  };

  const schema = yup.object({
    client_id: yup
      .string()
      .max(255, 'client_id не может превышать 255 символов')
      .required('Обязательное поле')
      .matches(/^[^\n ]*$/, {
        message: 'Идентификатор не может содержать пробелы',
      })
      .matches(/^[A-Za-z0-9_-]+$/, {
        message:
          'Может содержать латинские буквы (a-z), цифры (0-9), дефис (-) и нижнее подчёркивание (_)',
      }),
    client_secret: yup
      .string()
      .max(255, 'client_secret не может превышать 255 символов')
      .required('Обязательное поле'),
    domain: yup
      .string()
      .max(2000, 'Ссылка не может превышать 2000 символов')
      .test('is-url', 'Неверный формат ссылки', (value?: string) => {
        if (!value) return true;
        return isUrl(value);
      })
      .required('Обязательное поле'),
    redirect_uris: yup.array().of(redirectUriSchema).required(),
    post_logout_redirect_uris: yup.array().of(logoutUriSchema).required(),
    request_uris: yup.array().of(requestUriSchema).required(),
  });

  const methods = useForm<TClientSettingsInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      client_id: client.client_id,
      client_secret: client.client_secret,
      domain: client.domain,
      authorize_only_admins: client.authorize_only_admins,
      response_types: client.response_types,
      grant_types: client.grant_types,
      token_endpoint_auth_method: client.token_endpoint_auth_method,
      introspection_endpoint_auth_method: client.introspection_endpoint_auth_method,
      revocation_endpoint_auth_method: client.revocation_endpoint_auth_method,
      id_token_signed_response_alg: client.id_token_signed_response_alg,
      require_auth_time: client.require_auth_time,
      subject_type: client.subject_type,
      redirect_uris: client?.redirect_uris?.map((uri) => ({ value: uri })) || [{ value: '' }],
      post_logout_redirect_uris: client?.post_logout_redirect_uris?.length
        ? client.post_logout_redirect_uris.map((uri) => ({ value: uri }))
        : [{ value: '' }],
      request_uris: client?.request_uris?.length
        ? client.request_uris.map((uri) => ({ value: uri }))
        : [{ value: '' }],
    },
    mode: 'onChange',
  });

  const {
    register,
    control,
    setValue,
    setError,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
    watch,
  } = methods;

  useEffect(() => {
    if (updateClientResult.isSuccess) {
      dispatch(
        setNotice({
          type: ENoticeType.info,
          id: Math.random(),
          isRead: false,
          message: `Параметры сохранены`,
          timestamp: new Date().toString(),
        }),
      );
    }
  }, [updateClientResult]);

  const onSubmit: SubmitHandler<TClientSettingsInputs> = async (data) => {
    try {
      //TODO проверить это условие?
      if (data.redirect_uris.every((uri) => !uri.value)) {
        setError(`redirect_uris.0.value`, { message: 'Обязательное поле' });
        return;
      }
      if (Object.keys(errors).length) return;
      const payload = (Object.keys(dirtyFields) as Array<keyof typeof data>).reduce(
        (acc: Partial<IClientFull>, field) => {
          if (
            field === 'post_logout_redirect_uris' ||
            field === 'redirect_uris' ||
            field === 'request_uris'
          ) {
            acc[field] = data[field].reduce((dataAcc: string[], uri) => {
              if (uri.value) dataAcc.push(uri.value);
              return dataAcc;
            }, []);
            return { ...acc };
          }
          return { ...acc, [field]: data[field] };
        },
        {},
      );

      await updateClient({
        id: client?.client_id,
        data: payload,
      }).unwrap();
      reset(data);
    } catch (e) {
      console.log('err', e);
    }
  };

  const {
    fields: redirectUris,
    append: redirectAppend,
    remove: redirectRemove,
  } = useFieldArray({
    control,
    name: 'redirect_uris',
  });
  const {
    fields: logoutUris,
    append: logoutAppend,
    remove: logoutRemove,
  } = useFieldArray({
    control,
    name: 'post_logout_redirect_uris',
  });
  const {
    fields: requestUris,
    append: requestAppend,
    remove: requestRemove,
  } = useFieldArray({
    control,
    name: 'request_uris',
  });
  const watchResponseTypes = watch('response_types');
  const watchGrantTypes = watch('grant_types');
  const watchClientSecret = watch('client_secret');
  const isAdminClient = client.client_id === CLIENT_ID;

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsItem title="Параметры приложения">
            <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
              Идентификатор (client_id)
            </CustomTypography>
            <TextField
              {...register('client_id')}
              className="custom"
              variant="standard"
              error={!!errors.client_id}
              helperText={errors.client_id?.message}
              fullWidth
              disabled
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconWithTooltip
                        dataAttribute={'copy-provider-button'}
                        iconType="copy"
                        onClick={() => navigator.clipboard.writeText(client.client_id)}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
              Уникальный идентификатор приложения
            </CustomTypography>
            <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
              Секретный ключ (client_secret)
            </CustomTypography>
            {/* //TODO проверить ошибки поля */}
            <PasswordTextfield
              nameField={'client_secret'}
              showCopyButton
              disabled={isAdminClient}
            />
            <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
              Адрес приложения
            </CustomTypography>
            <TextField
              {...register('domain')}
              className="custom"
              variant="standard"
              error={!!errors.domain}
              helperText={errors.domain?.message}
              fullWidth
              disabled={isAdminClient}
            />
            <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
              Адрес приложения в формате «протокол://доменное имя:порт»
            </CustomTypography>
            <div className={styles['switch-wrapper']}>
              <div>
                <CustomTypography className={clsx('text-14')}>Ограниченный доступ</CustomTypography>
                <CustomTypography className={clsx('text-12')} color="grey">
                  Вход в приложение доступно только для пользователей с правами “Администратор”
                </CustomTypography>
              </div>
              <Switch
                defaultChecked={client?.authorize_only_admins || false}
                {...register('authorize_only_admins')}
              />
            </div>

            {redirectUris.map((uri, index) => {
              return (
                <div
                  className={index === 0 ? styles.inputFields : styles.inputFieldsMin}
                  key={uri.id}
                >
                  <ArrayTextFields
                    name="redirect_uris"
                    title="Возвратный URL #"
                    description="Адрес, на который пользователь переадресовывается после авторизации"
                    onClickAdd={() => redirectAppend({ value: '' })}
                    onClickDelete={() => redirectRemove(index)}
                    index={index}
                    isError={!!errors?.redirect_uris?.[index]}
                    errorText={errors?.redirect_uris?.[index]?.value?.message}
                    disabled={isAdminClient}
                    required
                  />
                </div>
              );
            })}

            {logoutUris.map((uri, index) => {
              return (
                <div
                  className={index === 0 ? styles.inputFields : styles.inputFieldsMin}
                  key={uri.id}
                >
                  <ArrayTextFields
                    name="post_logout_redirect_uris"
                    title="URL выхода из системы #"
                    description="Адрес, на который переадресовывается пользователь после выхода. Если значение не указано, то используется «Возвратный URL»"
                    onClickAdd={() => logoutAppend({ value: '' })}
                    onClickDelete={() => logoutRemove(index)}
                    index={index}
                    isError={!!errors?.post_logout_redirect_uris?.[index]}
                    errorText={errors?.post_logout_redirect_uris?.[index]?.value?.message}
                    disabled={isAdminClient}
                  />
                </div>
              );
            })}

            {requestUris.map((uri, index) => {
              return (
                <div
                  className={index === 0 ? styles.inputFields : styles.inputFieldsMin}
                  key={uri.id}
                >
                  <ArrayTextFields
                    name="request_uris"
                    title="URL запроса аутентификации или восстановления после аутентификации #"
                    description="Адрес, на который переадресовывается пользователь после выхода. Если значение не указано, то используется «Возвратный URL»"
                    onClickAdd={() => requestAppend({ value: '' })}
                    onClickDelete={() => requestRemove(index)}
                    index={index}
                    isError={!!errors?.request_uris?.[index]}
                    errorText={errors?.request_uris?.[index]?.value?.message}
                    disabled={isAdminClient}
                  />
                </div>
              );
            })}

            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Тип ответов (response_types)
            </CustomTypography>
            <div className={styles['type-buttons-wrapper']}>
              {Object.values(EResponseTypes)?.map((type) => (
                <Button
                  variant={
                    watchResponseTypes?.find((findType) => findType === type)
                      ? 'contained'
                      : 'outlined'
                  }
                  className={styles.typeButton}
                  disabled={
                    isAdminClient ||
                    (type === EResponseTypes.code || type === EResponseTypes.none
                      ? false
                      : !watchGrantTypes?.includes(EGrantTypes.implicit))
                  }
                  onClick={() => {
                    setValue(
                      'response_types',
                      watchResponseTypes.find((findType) => findType === type)
                        ? watchResponseTypes.filter((filterType) => filterType !== type)
                        : [...watchResponseTypes, type],
                      { shouldDirty: true },
                    );
                  }}
                  key={type}
                >
                  {type}
                </Button>
              ))}
            </div>
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Типы предоставления доступа (grant_types)
            </CustomTypography>
            <div className={styles['type-buttons-wrapper']}>
              {Object.values(EGrantTypes).map((type) => (
                <Button
                  className={styles.typeButton}
                  variant={
                    watchGrantTypes?.find((findType) => findType === type)
                      ? 'contained'
                      : 'outlined'
                  }
                  onClick={() => {
                    const newGrantTypes = watchGrantTypes.find((findType) => findType === type)
                      ? watchGrantTypes.filter((filterType) => filterType !== type)
                      : [...watchGrantTypes, type];
                    if (!newGrantTypes.includes(EGrantTypes.implicit))
                      setValue(
                        'response_types',
                        watchResponseTypes.filter(
                          (type) => type === EResponseTypes.code || type === EResponseTypes.none,
                        ),
                      );

                    setValue('grant_types', newGrantTypes, { shouldDirty: true });
                  }}
                  disabled={isAdminClient}
                  key={type}
                >
                  {type}
                </Button>
              ))}
            </div>
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Метод аутентификации клиента для конечной точки получения токена
              (token_endpoint_auth_method)
            </CustomTypography>
            <Controller
              control={control}
              name="token_endpoint_auth_method"
              defaultValue={EAuthMethodType.client_secret_basic}
              render={({ field }) => (
                <CustomSelect
                  disabled={isAdminClient}
                  style={{ width: '100%', marginBottom: 32 }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {Object.keys(EAuthMethodType).map((variant) => (
                    <MenuItem key={variant} value={variant} className="custom-select">
                      {variant}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            />
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Метод аутентификации, используемый при доступе к конечной точке проверки токена
              (introspection_endpoint_auth_method)
            </CustomTypography>
            <Controller
              control={control}
              name="introspection_endpoint_auth_method"
              defaultValue={EAuthMethodType.client_secret_basic}
              render={({ field }) => (
                <CustomSelect
                  disabled={isAdminClient}
                  style={{ width: '100%', marginBottom: 32 }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {Object.keys(EAuthMethodType).map((variant) => (
                    <MenuItem key={variant} value={variant} className="custom-select">
                      {variant}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            />
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Метод аутентификации, используемый при доступе к конечной точке отзыва токенов
              (revocation_endpoint_auth_method)
            </CustomTypography>
            <Controller
              control={control}
              name="revocation_endpoint_auth_method"
              defaultValue={EAuthMethodType.client_secret_basic}
              render={({ field }) => (
                <CustomSelect
                  disabled={isAdminClient}
                  style={{ width: '100%', marginBottom: 32 }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {Object.keys(EAuthMethodType).map((variant) => (
                    <MenuItem key={variant} value={variant} className="custom-select">
                      {variant}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            />
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Алгоритм подписи, используемый при создании подписанного ID-токена
              (id_token_signed_response_alg)
            </CustomTypography>
            <Controller
              control={control}
              name="id_token_signed_response_alg"
              render={({ field }) => (
                <CustomSelect
                  disabled={isAdminClient}
                  style={{ width: '100%' }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {Object.keys(ESigningAlgTypes).map((variant) => (
                    <MenuItem key={variant} value={variant} className="custom-select">
                      {variant}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            />
            <div className={styles['switch-wrapper']}>
              <CustomTypography className={clsx('text-14')}>
                Проверка наличия времени Аутентификации (require_auth_time)
              </CustomTypography>
              <Switch
                defaultChecked={client?.require_auth_time || false}
                {...register('require_auth_time')}
              />
            </div>
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Способ передачи ID пользователя в идентификационном токене (subject_type)
            </CustomTypography>
            <Controller
              control={control}
              name="subject_type"
              defaultValue={ESubjectTypeVariant.public}
              render={({ field }) => (
                <CustomSelect
                  disabled={isAdminClient}
                  style={{ width: '100%', marginBottom: 32 }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {['public', 'pairwise'].map((type) => (
                    <MenuItem key={type} value={type} className="custom-select">
                      {type}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            />
            <div className={styles['submit-buttons']}>
              <Button
                className={styles['create-button']}
                type="submit"
                variant="custom"
                disabled={isObjectEmpty(dirtyFields)}
              >
                Сохранить
              </Button>
            </div>
          </SettingsItem>
        </form>
      </FormProvider>
    </>
  );
};
